import { Eye, EyeOff } from 'lucide-react'
import { useState, type InputHTMLAttributes } from 'react'

export const SHOW_PASSWORD = 'Show password'
export const HIDE_PASSWORD = 'Hide password'

export interface PasswordFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'autoComplete' | 'id'
> {
  id: string
  /** `current-password` (A-01) or `new-password` (A-04, A-03). Never changed by the toggle. */
  autoComplete: 'current-password' | 'new-password'
  /** Hides the text again when it changes (bumped after a successful submit). */
  resetKey?: unknown
}

/**
 * A password input with an eye-icon button inside it (Frontend §5.2 `PasswordField`, UserFlows §3 "Show/hide
 * password"). The button is a real button in the tab order, labelled "Show password" / "Hide password", with
 * `aria-pressed` and `aria-controls`. It switches `type` between `password` and `text` and never touches
 * `autocomplete`, so password managers fill and save as before. The text is hidden again on `resetKey` change and on
 * unmount (leaving the page).
 */
export function PasswordField({ id, autoComplete, resetKey, className = 'input', ...input }: PasswordFieldProps) {
  // Shown for one `resetKey` only: a new key hides the text again without an effect.
  const [shownFor, setShownFor] = useState<{ key: unknown } | null>(null)
  const shown = shownFor !== null && Object.is(shownFor.key, resetKey)
  return (
    <div className="pw-field">
      <input {...input} id={id} className={className} type={shown ? 'text' : 'password'} autoComplete={autoComplete} />
      <button
        type="button"
        className="pw-toggle"
        aria-label={shown ? HIDE_PASSWORD : SHOW_PASSWORD}
        aria-pressed={shown}
        aria-controls={id}
        onClick={() => {
          setShownFor(shown ? null : { key: resetKey })
        }}
      >
        {shown ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
      </button>
    </div>
  )
}
